import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { LoginResponse, PapelUsuario, Usuario } from '@/contratos'
import { EVENTO_SESSAO_EXPIRADA, get, post, TOKEN_KEY } from '@/api/client'

interface AuthState {
  usuario: Usuario | null
  carregando: boolean
  entrar: (email: string, senha: string) => Promise<void>
  sair: () => void
  temPapel: (...papeis: PapelUsuario[]) => boolean
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setCarregando(false)
      return
    }
    get<Usuario>('/auth/me')
      .then(setUsuario)
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setCarregando(false))
  }, [])

  const entrar = useCallback(async (email: string, senha: string) => {
    // Um 403 com ERRO_CADASTRO_PENDENTE sobe como ApiError e e tratado na tela
    // de login: nao e senha errada, e cadastro aguardando liberacao (FA-04).
    const { token, usuario: u } = await post<LoginResponse>('/auth/login', { email, senha })
    localStorage.setItem(TOKEN_KEY, token)
    setUsuario(u)
  }, [])

  const sair = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setUsuario(null)
  }, [])

  // O cliente HTTP dispara este evento quando um 401 nao pode ser renovado. Aqui
  // ele vira logout, e os Guards levam o usuario de volta ao /login.
  useEffect(() => {
    const aoExpirar = () => setUsuario(null)
    window.addEventListener(EVENTO_SESSAO_EXPIRADA, aoExpirar)
    return () => window.removeEventListener(EVENTO_SESSAO_EXPIRADA, aoExpirar)
  }, [])

  const temPapel = useCallback(
    (...papeis: PapelUsuario[]) => (usuario ? papeis.includes(usuario.papel) : false),
    [usuario],
  )

  const value = useMemo(
    () => ({ usuario, carregando, entrar, sair, temPapel }),
    [usuario, carregando, entrar, sair, temPapel],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>')
  return ctx
}
